import {
  ATTRIBUTES,
  debounce,
  fetchConfig,
  ON_CHANGE_DEBOUNCE_TIMER,
  PUB_SUB_EVENTS,
  publish,
  PubSubEvent,
  SELECTORS,
  subscribe,
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import {type ShopifySectionRenderingSchema} from '@/scripts/types/theme'
import {type CartDrawer} from '@/scripts/cart/cart-drawer'
// import {isArray} from 'lodash-es';
import {UcoastEl} from '@/scripts/core/UcoastEl'

import {BundleChild, BundleChildRaw, fromBundleChildRaw, isBundleChildRaw} from "@/scripts/product/types";
import { CartChangeResponse } from '@/scripts/types/responses';


interface CartItemDetail {
	key: string;
	quantity: number;
	variantId: string;
	sellingPlanId: string;
	isBundleItem: boolean;
	bundleChildren: BundleChild[];
	element: HTMLElement;
	properties?: {
		_refill_variant_id?: string;
		_parent_variant_id?: string;

	}
}

export class CartItems extends UcoastEl {
	// static
	static htmlSelector = 'cart-items'
	static selectors = {
		element: 'cart-items',
		itemLink: '[data-uc-cart-item-name]',
		lineItemStatus: '[data-uc-cart-status]',
		errors: '[data-uc-cart-errors]',
		liveRegionText: '#CartPage-LiveRegionText',
		main: '#main-cart-items',
		cartDrawerInner: '[data-uc-cart-drawer-inner]',
		cartDrawerInnerEmpty: '[data-uc-cart-drawer-inner-empty]',
		item: '[data-uc-cart-item]',
		itemErrorText: '[data-uc-cart-item-error-text]',
		footer: '#CartPage-Footer',
		loadingOverlay: '[data-uc-loading-overlay]',
		// the following selectors are partial - they will be concatenated with the line ID
		line: '#CartPage-Item', // ex: `${this.instanceSelectors.line}-${line}`
		lineQuantity: '#CartPage-LineItemQuantity', // ex: `${this.instanceSelectors.lineQuantity}-${line}`
		lineError: '#CartPage-LineItemError', // ex: `${this.instanceSelectors.lineError}-${line}`
	}
	// TODO: refactor to global attributes selector
	static attributes = {
		disabled: 'data-uc-disabled',
	}

	// instance specific property types
	instanceSelectors = CartItems.selectors
	lineItemStatusElement: HTMLElement
	cartUpdateUnsubscriber?: () => void = undefined

	// init
	constructor() {
		super()
		this.setInstanceSelectors()
		this.lineItemStatusElement = q.rs(this.instanceSelectors.lineItemStatus)

		const debouncedOnChange = debounce((event: Event) => {
			this.onChange(event)
		}, ON_CHANGE_DEBOUNCE_TIMER)

		this.addEventListener('change', debouncedOnChange.bind(this))
		this.setupSubscriptionReplacement()
	}

	// have to instantiate selectors here so they can be different for CartDrawerItems
	setInstanceSelectors() {
		this.instanceSelectors = CartItems.selectors
	}

	// from here on is default Dawn CartItems -> typescript
	override connectedCallback() {
		this.cartUpdateUnsubscriber = subscribe(
			PUB_SUB_EVENTS.cartUpdate,
			(pubSubEvent: PubSubEvent) => {
				if (pubSubEvent && 'source' in pubSubEvent && pubSubEvent.source === 'cart-items') {
					return
				}
				this.onCartUpdate()
			}
		)
	}

	override disconnectedCallback() {
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber()
		}
	}

	onChange(event: Event) {
		const target = q.rt<Event, HTMLInputElement>(event)
    if (target.id === 'ucoast_discount') return;
		const activeElement = document.activeElement as HTMLElement
		const activeElementName = activeElement ? q.oa(activeElement, 'name') : undefined
		if (!activeElementName) {
			console.warn('no active element name, focusing default cart')
		}

    const targetItemKey = q.ra(target, 'data-uc-cart-item-key')
    // removed trapFocus so no name needed
		// this.updateQuantity(targetIndex, target.value, activeElementName)
    this.updateQuantityByKey(targetItemKey, target.value)
	}

	onCartUpdate() {
		fetch(`${window.routes.cart_url}?section_id=main-cart-items`)
			.then((response) => response.text())
			.then((responseText) => {
				const html = new DOMParser().parseFromString(responseText, 'text/html')
				// reference CartItems selectors here since this returns then inner HTML I guess
				const source = q.os(CartItems.selectors.element, html.documentElement)
        if (!source) {
          console.warn('cart-items not found')
          return
        }
				this.innerHTML = source.innerHTML
			})
			.catch((e) => {
				console.error(e)
			})
	}

	getSectionsToRender(): ShopifySectionRenderingSchema[] {
		return [
			{
				id: 'main-cart-items',
				section: q.ra(q.rs(this.instanceSelectors.main), 'data-id'),
				selector: '.js-contents',
			},
			{
				id: 'CartIconBubble',
				section: 'cart-icon-bubble',
				selector: SELECTORS.cartLink,
			},
			{
				id: 'CartPage-LiveRegionText',
				section: 'cart-live-region-text',
				selector: '.shopify-section',
			},
			{
				id: 'CartPage-Footer',
				section: q.ra(q.rs(this.instanceSelectors.footer), 'data-id'),
				selector: '.js-contents',
			},
		]
	}

	updateQuantityByKey(key: string, _quantity: string /*, name?: string */) {
		this.enableLoadingByKey(key)

    const quantity = parseInt(_quantity);

    const items = this.cartItems;
    const targetItem = items.find(item => item.key === key);
    if (!targetItem) {
      throw Error(`Could not find item with key ${key}`);
    }

    if (targetItem.isBundleItem) {
      // ignore, cannot update manually
      return;
    }
    const updates: Record<string, number> = {
      [targetItem.key]: quantity,
    };

	if (targetItem.bundleChildren.length > 0) {
		// adjust bundle children too
		const children = targetItem.bundleChildren.map(child => {
			const item = items.find(i => i.variantId === child.variantId.toString() && i.sellingPlanId === (child.sellingPlanId?.toString() ?? ''));
			if (!item) {
				return {
					key: child.variantId,
					quantity: 0,
					sellingPlanId: child.sellingPlanId,
					maxQuantity: child.maxAmountInCart ?? Infinity,
				};
			}
			return {
				...item,
				maxQuantity: child.maxAmountInCart ?? Infinity,
			};
		});

		const quantityDelta = targetItem.quantity - quantity;
		children.forEach(child => {
			updates[child.key] = Math.max(0, Math.min(child.quantity - quantityDelta, child.maxQuantity));
		});
	}
	if (targetItem.properties) {
		const parentVariant = targetItem.properties._parent_variant_id
		const refillVariant = targetItem.properties._refill_variant_id
		if (refillVariant) {
			updates[refillVariant] = 0;
		} else if (parentVariant) {
			updates[parentVariant] = 0;
		}
	}


		const body = JSON.stringify({
      		updates,
			sections: this.getSectionsToRender().map((section) => section.section),
			sections_url: window.location.pathname,
		})

		fetch(`${window.routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
			.then((response) => {
				return response.text()
			})
			.then((state) => {
				const parsedState = JSON.parse(state)
				const quantityElement = this.getQuantityInputByVariantId(targetItem.variantId);
				const items = document.querySelectorAll(this.instanceSelectors.item)

				if (parsedState.errors) {
					quantityElement.value = q.ra(quantityElement, 'value')
					this.updateLiveRegionsByVariantId(targetItem.variantId, parsedState.errors)
					return
				}

				const cartDrawerWrapper = q.os<CartDrawer>('cart-drawer')
				const cartFooter = q.os(this.instanceSelectors.footer)
				if (parsedState.item_count === 0) {
					this.setAttribute(ATTRIBUTES.cartEmpty, '')
					cartFooter && cartFooter.setAttribute(ATTRIBUTES.cartEmpty, '')
					cartDrawerWrapper && cartDrawerWrapper.setAttribute(ATTRIBUTES.cartEmpty, '')
				} else {
					this.removeAttribute(ATTRIBUTES.cartEmpty)
					cartFooter && cartFooter.removeAttribute(ATTRIBUTES.cartEmpty)
					cartDrawerWrapper && cartDrawerWrapper.removeAttribute(ATTRIBUTES.cartEmpty)
				}

				this.getSectionsToRender().forEach((section: ShopifySectionRenderingSchema) => {
					const sectionEl = q.os(`#${section.id}`)
					if (!sectionEl) {
						console.error(`Could not find section with id ${section.id}`);
						return;
					}
					const elementToReplace =
						(section.selector && sectionEl.querySelector(section.selector)) || sectionEl
					if (!section.section) throw new Error('no section.section')
					if (!section.selector) throw new Error('no section.selector')
					if (section.id === 'CartIconBubble') {
						const cartIconHTML = this.getSectionInnerHTML(
							parsedState.sections[section.section],
							section.selector
						)

						q.rl('[data-uc-cart-icon-bubble]').forEach(el => {
							el.innerHTML = cartIconHTML
							const countEl = q.rs('[data-count]', el)
							const count = parseInt(q.ra(countEl, 'data-count'))
							const headerCartButton = q.rs('[data-uc-header-cart-button]')
							console.log({count})
							if (count === 0) {
								headerCartButton.classList.add('empty')
							} else {
								headerCartButton.classList.remove('empty')
							}
						})

					} else {
						elementToReplace.innerHTML = this.getSectionInnerHTML(
							parsedState.sections[section.section],
							section.selector
						)
					}
				})

        // use == instead of === cuz item.id is number
				const updatedValue = (parsedState.items as any[]).find(item => item.id == key)?.quantity;
				let message = ''
				if (
					items.length === parsedState.items.length &&
					updatedValue !== parseInt(quantityElement.value)
				) {
					if (typeof updatedValue === 'undefined') {
						message = window.cartStrings.error
					} else {
						message = window.cartStrings.quantityError.replace(
							'[quantity]',
							updatedValue
						)
					}
				}
				this.updateLiveRegionsByVariantId(key, message)

				// const lineItem = q.os(`${this.instanceSelectors.line}-${line}`)
				// if (lineItem?.querySelector(`[name="${name}"]`)) {
				// 	cartDrawerWrapper
				// 		? trapFocus(cartDrawerWrapper, q.rs(`[name="${name}"]`, lineItem))
				// 		: q.rs(`[name="${name}"]`, lineItem).focus()
				// } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
				// 	trapFocus(
				// 		q.rs(this.instanceSelectors.cartDrawerInnerEmpty, cartDrawerWrapper),
				// 		q.os('a', cartDrawerWrapper)
				// 	)
				// } else if (document.querySelector(this.instanceSelectors.item) && cartDrawerWrapper) {
				// 	trapFocus(cartDrawerWrapper, q.rs(this.instanceSelectors.itemLink))
				// }

				publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' })
			})
			.catch((error) => {
				console.error(error)
				this.querySelectorAll(this.instanceSelectors.loadingOverlay).forEach((overlay) =>
					overlay.classList.add('hidden')
				)
				const errors = q.rs(this.instanceSelectors.errors)
				errors.textContent = window.cartStrings.error
			})
			.finally(() => {
				this.disableLoadingByKey(key)
			})
	}

  getCartItemFromEl(itemEl: HTMLElement): CartItemDetail {
    const key = itemEl.dataset['ucCartItemKey'] ?? '';
    const quantity = parseInt(itemEl.dataset['ucCartVolumeQuantity'] ?? '0');
    const variantId = itemEl.dataset['ucCartVolumeVariant'] ?? '';
    const sellingPlanId = itemEl.dataset['ucCartVolumeSellingPlanId'] ?? '';
    const isBundleItem = itemEl.dataset['ucIsBundleItem'] === 'true';
	const quantityEl = q.os('input', itemEl)
	  console.log(quantityEl, 'quantityEl')
	  let childVariantId: string | undefined;
	  let parentVariantId: string | undefined;
	  if (quantityEl) {
		  childVariantId = quantityEl.dataset['ucRefillVariantId'];
		  parentVariantId = quantityEl.dataset['ucParentVariant'];
	  }
    const bundleChildren = JSON.parse(itemEl.dataset['ucBundleChildren'] ?? '[]');

    if (bundleChildren && !Array.isArray(bundleChildren)) {
      throw Error('Bundle children should be an array');
    }

    return {
      key,
      quantity,
      variantId,
      sellingPlanId,
      isBundleItem,
      bundleChildren: bundleChildren?.filter((child: unknown): child is BundleChildRaw => isBundleChildRaw(child)).map((b: BundleChildRaw) => fromBundleChildRaw(b)) ?? [],
      element: itemEl,
		properties: {
			_parent_variant_id: parentVariantId,
			_refill_variant_id: childVariantId
	  	}
    };
  }

  get cartItems() {
    const items = q.ol(this.instanceSelectors.item, this);
    if (!items) return [];
    return Array.from(items).map(itemEl => this.getCartItemFromEl(itemEl));
  }

	updateLiveRegions(line: string, message: string) {
		const lineItemError = q.os(`${this.instanceSelectors.lineError}-${line}`)
		if (lineItemError) {
			const errorText = q.rs(this.instanceSelectors.itemErrorText, lineItemError)
			errorText.innerHTML = message
		}

		this.lineItemStatusElement.setAttribute('aria-hidden', 'true')

		const cartStatus = q.rs(this.instanceSelectors.liveRegionText)
		cartStatus.setAttribute('aria-hidden', 'false')

		setTimeout(() => {
			cartStatus.setAttribute('aria-hidden', 'true')
		}, 1000)
	}
  updateLiveRegionsByVariantId(id: string, message: string) {
    const lineItemError = q.os(`[data-uc-cart-volume-variant="${id}"]`);
    if (lineItemError) {
      const errorText = q.rs(this.instanceSelectors.itemErrorText, lineItemError)
      errorText.innerHTML = message
    }
  }
  getLineItemErrorByLine(line: string) {
    return q.os(`${this.instanceSelectors.lineError}-${line}`);
  }
  getLineItemErrorByVariantId(id: string) {
    return q.os(`[data-uc-cart-volume-variant="${id}"]`);
  }

	getSectionInnerHTML(html: string, selector: string) {
		const newDocument = new DOMParser().parseFromString(html, 'text/html')
		const newElement = q.rs(selector, newDocument.documentElement)
		return newElement.innerHTML
	}

	enableLoadingByLine(line: string) {
		this.getCartItemLoadingOverlayElementsByLine(line)?.forEach((overlay) => overlay.classList.remove('hidden'))
    this._enableLoading();
	}
  enableLoadingByKey(key: string) {
    this.getCartItemLoadingOverlayElementsByKey(key)?.forEach((overlay) => overlay.classList.remove('hidden'))
    this._enableLoading();
  }
  enableLoadingByVariantId(id: string) {
    this.getCartItemLoadingOverlayElementsByVariantId(id)?.forEach((overlay) => overlay.classList.remove('hidden'))
    this._enableLoading();
  }

  private _enableLoading() {
    const mainCartItems = this.getMainCartItems()
    mainCartItems.setAttribute(CartItems.attributes.disabled, '')

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', 'false')
  }

	getMainCartItems() {
		return q.rs(this.instanceSelectors.main)
	}

	getCartItemLoadingOverlayElementsByLine(line: string) {
		return q.ol(
			`${this.instanceSelectors.line}-${line} ${this.instanceSelectors.loadingOverlay}`,
			this
		)
	}
  getCartItemLoadingOverlayElementsByKey(key: string) {
    return q.ol(
      `[data-uc-cart-item-key="${key}"] ${this.instanceSelectors.loadingOverlay}`,
      this
    )
  }
  getCartItemLoadingOverlayElementsByVariantId(id: string) {
    return q.ol(
      `[data-uc-cart-volume-variant="${id}"] ${this.instanceSelectors.loadingOverlay}`,
      this
    )
  }
  getQuantityInputByVariantId(id: string):HTMLInputElement {
    return q.rs(
      `input[data-quantity-variant-id="${id}"]`,
      this,
    );
  }

	disableLoading(line: string) {
		this.getMainCartItems().removeAttribute(CartItems.attributes.disabled)
		this.getCartItemLoadingOverlayElementsByLine(line)?.forEach((overlay) => overlay.classList.add('hidden'))
	}
  disableLoadingByKey(key: string) {
    this.getMainCartItems().removeAttribute(CartItems.attributes.disabled)
    this.getCartItemLoadingOverlayElementsByKey(key)?.forEach((overlay) => overlay.classList.add('hidden'))
  }
  disableLoadingByVariantId(id: string) {
    this.getMainCartItems().removeAttribute(CartItems.attributes.disabled)
    this.getCartItemLoadingOverlayElementsByVariantId(id)?.forEach((overlay) => overlay.classList.add('hidden'))
  }

  private setupSubscriptionReplacement() {
    this.addEventListener('click', async (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.dataset.action === 'replace-with-subscription') {
        event.preventDefault();
        const button = target;
        const variantId = button.dataset.variantId;
        const sellingPlanId = button.dataset.sellingPlanId;
        const quantity = parseInt(button.dataset.quantity || '1', 10);
        const lineItemKey = button.dataset.lineItemKey;

        if (!variantId || !sellingPlanId || !lineItemKey) {
          console.error('Missing required data attributes for subscription replacement');
          return;
        }

        try {
          this.enableLoadingByKey(lineItemKey);

          await this.changeCart(lineItemKey, 0);
          await this.addToCart(variantId, quantity, sellingPlanId);

          const cartDrawer = q.os<CartDrawer>('cart-drawer');
          if (cartDrawer) {
            await cartDrawer.refresh();
          } else {
            await this.onCartUpdate();
          }
        } catch (error) {
          console.error('Error updating cart:', error);
        } finally {
          this.disableLoadingByKey(lineItemKey);
        }
      }
    });
  }

  private async changeCart(id: string, quantity: number): Promise<CartChangeResponse> {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, quantity }),
    });
    return response.json();
  }

  private async addToCart(id: string, quantity: number, selling_plan: string): Promise<CartChangeResponse> {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{ id, quantity, selling_plan }],
      }),
    });
    return response.json();
  }
}
